import { isValidHexColor, normalizeHexColor } from "./hex-color";

function normalizedSet(colors: readonly string[]): ReadonlySet<string> {
  return new Set(colors.filter(isValidHexColor).map(normalizeHexColor));
}

/** カスタム色を重複させず追加し、上限超過分を古い順に取り除く。 */
export function addColorToList(
  current: readonly string[],
  color: string,
  presets: readonly string[],
  limit: number
): readonly string[] {
  if (!isValidHexColor(color)) return current;
  const normalized = normalizeHexColor(color);
  if (normalizedSet(presets).has(normalized)) return current;
  if (normalizedSet(current).has(normalized)) return current;
  return [...current, normalized].slice(-Math.max(0, limit));
}

export interface UpdateColorListResult {
  readonly colors: readonly string[];
  readonly applied: boolean;
}

/** 指定位置の色だけを置換し、プリセット・別カスタム色との重複を解消する。 */
export function updateColorInList(
  current: readonly string[],
  previous: string,
  next: string,
  presets: readonly string[]
): UpdateColorListResult {
  if (!isValidHexColor(next)) return { colors: current, applied: false };
  const normalizedPrevious = isValidHexColor(previous) ? normalizeHexColor(previous) : previous;
  const normalizedNext = normalizeHexColor(next);
  const previousIndex = current.findIndex(
    (color) => isValidHexColor(color) && normalizeHexColor(color) === normalizedPrevious
  );
  if (previousIndex < 0 || normalizedPrevious === normalizedNext) {
    return { colors: current, applied: false };
  }
  if (normalizedSet(presets).has(normalizedNext)) {
    return {
      colors: current.filter((_, index) => index !== previousIndex),
      applied: true,
    };
  }

  const colors = current.flatMap((color, index) => {
    if (index === previousIndex) return [normalizedNext];
    if (isValidHexColor(color) && normalizeHexColor(color) === normalizedNext) return [];
    return [color];
  });
  return { colors, applied: true };
}

/** 指定色だけを正規化比較で取り除く。 */
export function removeColorFromList(current: readonly string[], color: string): readonly string[] {
  if (!isValidHexColor(color)) return current;
  const normalized = normalizeHexColor(color);
  const colors = current.filter(
    (candidate) => !isValidHexColor(candidate) || normalizeHexColor(candidate) !== normalized
  );
  return colors.length === current.length ? current : colors;
}
